import { useEffect, useRef, useState } from "react";
import { GUEST_MENUS, guestMenuLabel } from "../../shared/guests";
import { api } from "../api";
import { messageOf } from "./ui";

type GuestSeat = { name: string; table: string };

type GuestStatus = {
  state: "draft" | "sent" | "submitted";
  sent_at: string | null;
  submitted_at: string | null;
};

type GuestBody = {
  guest_count: number;
  menu: string;
  menu_label: string;
  venue_name: string;
};

type Loaded = {
  body: GuestBody | null;
  status: GuestStatus | null;
  guests: GuestSeat[];
};

export function GuestFinalize({
  visitId,
  canEdit,
  suggestedCount,
  suggestedMenu,
  venueName,
  onSent,
}: {
  visitId: string | null;
  canEdit: boolean;
  suggestedCount: number | null;
  suggestedMenu: string;
  venueName: string;
  onSent?: () => void;
}) {
  const [countRaw, setCountRaw] = useState(suggestedCount == null ? "" : String(suggestedCount));
  const [menu, setMenu] = useState(suggestedMenu);
  const [status, setStatus] = useState<GuestStatus | null>(null);
  const [guests, setGuests] = useState<GuestSeat[]>([]);
  const [ready, setReady] = useState(!visitId);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const seedRef = useRef({ count: suggestedCount, menu: suggestedMenu });
  const venueRef = useRef(venueName);
  seedRef.current = { count: suggestedCount, menu: suggestedMenu };
  venueRef.current = venueName;

  useEffect(() => {
    if (!visitId) return;
    let cancel = false;
    api<Loaded>(`/api/tastings/${visitId}/guests`)
      .then((result) => {
        if (cancel) return;
        if (result.body) {
          setCountRaw(String(result.body.guest_count));
          setMenu(result.body.menu);
          setGuests(result.guests);
          setStatus(result.status);
        } else {
          const seed = seedRef.current;
          setCountRaw(seed.count == null ? "" : String(seed.count));
          setMenu(seed.menu);
        }
        setReady(true);
      })
      .catch((reason: unknown) => {
        if (cancel) return;
        setError(messageOf(reason));
        setReady(true);
      });
    return () => {
      cancel = true;
    };
  }, [visitId]);

  const menuText = GUEST_MENUS.some((item) => item.value === menu) ? guestMenuLabel(menu) : menu;
  const locked = !canEdit;

  function payload() {
    return { guest_count: Number(countRaw), menu, venue_name: venueRef.current };
  }

  function invalid(): string | null {
    if (!countRaw.trim()) return "Γράψε τον αριθμό των καλεσμένων.";
    if (!menu.trim()) return "Διάλεξε το μενού.";
    return null;
  }

  async function save() {
    if (!visitId || !canEdit || saving || sending) return;
    const problem = invalid();
    if (problem) {
      setError(problem);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const result = await api<Loaded>(`/api/tastings/${visitId}/guests`, { method: "PUT", body: JSON.stringify(payload()) });
      apply(result);
      setNote("Αποθηκεύτηκε.");
    } catch (reason: unknown) {
      setError(messageOf(reason));
    } finally {
      setSaving(false);
    }
  }

  async function send() {
    if (!visitId || !canEdit || saving || sending) return;
    const problem = invalid();
    if (problem) {
      setError(problem);
      return;
    }
    setSending(true);
    setError(null);
    try {
      const result = await api<{ view: Loaded; sent: boolean }>(`/api/tastings/${visitId}/guests/send`, { method: "POST", body: JSON.stringify(payload()) });
      apply(result.view);
      setNote(result.sent ? "Το mail με τη φόρμα καλεσμένων έφυγε." : "Δεν στάλθηκε.");
      if (result.sent) onSent?.();
    } catch (reason: unknown) {
      setError(messageOf(reason));
    } finally {
      setSending(false);
    }
  }

  function apply(result: Loaded) {
    if (result.body) {
      setCountRaw(String(result.body.guest_count));
      setMenu(result.body.menu);
    }
    setGuests(result.guests);
    setStatus(result.status);
  }

  return (
    <article className="panel guest-finalize">
      <h2>Οριστικοποίηση καλεσμένων</h2>
      <p className="meta">Ο αριθμός έρχεται από τα προηγούμενα βήματα. Μπορείς να τον αλλάξεις πριν φύγει το mail.</p>
      {error ? <p className="banner error">{error}</p> : null}
      {note ? <p className="banner">{note}</p> : null}
      {status?.state === "submitted" ? <p>Ο πελάτης έστειλε τη λίστα με τα ονόματα και τα τραπέζια.</p> : status?.state === "sent" ? <p>Στάλθηκε. Ο πελάτης δεν έχει στείλει ονόματα ακόμα.</p> : null}
      {!ready ? <p>Φόρτωση οριστικοποίησης…</p> : (
        <>
          <label>Καλεσμένοι
            <input type="number" inputMode="numeric" min={1} step={1} value={countRaw} disabled={locked} onChange={(event) => { setCountRaw(event.target.value.replace(/\D/g, "").slice(0, 6)); setNote(null); }} />
          </label>
          <h2>Οριστικοποίηση μενού</h2>
          <p className="meta">Το μενού που έχει ήδη επιλεγεί. Διάλεξε Α, Β ή Γ, ή άλλαξε την ονομασία.</p>
          <div className="choice-row">
            {GUEST_MENUS.map((item) => (
              <button key={item.value} type="button" className="choice" aria-pressed={menu === item.value} disabled={locked} onClick={() => { setMenu(item.value); setNote(null); }}>
                {item.short}
              </button>
            ))}
          </div>
          <label>Μενού
            <input value={menuText} disabled={locked} onChange={(event) => { setMenu(typedMenu(event.target.value)); setNote(null); }} />
          </label>
          {guests.length ? (
            <>
              <h3>Λίστα που έστειλε ο πελάτης</h3>
              <ul className="guest-submitted">
                {guests.map((guest, index) => <li key={`${guest.name}-${guest.table}-${index}`}>{guest.name} — τραπέζι {guest.table}</li>)}
              </ul>
            </>
          ) : null}
          {canEdit && visitId ? (
            <div className="actions">
              <button type="button" className="secondary" onClick={() => void save()} disabled={saving || sending}>{saving ? "Αποθήκευση…" : "Αποθήκευση"}</button>
              <button type="button" onClick={() => void send()} disabled={saving || sending}>{sending ? "Αποστολή…" : "Αποστολή"}</button>
            </div>
          ) : null}
        </>
      )}
    </article>
  );
}

function typedMenu(value: string): string {
  const match = GUEST_MENUS.find((item) => item.label === value.trim() || item.short === value.trim());
  return match ? match.value : value;
}
